/*
------------------------------------------------------------------------
PostHog Analytics Plugin for Vue 3

Initializes PostHog client and provides it to the Vue application.
Captures pageviews automatically via router integration.

@see https://posthog.com/docs/libraries/vue-js
------------------------------------------------------------------------
*/

import posthog from 'posthog-js';
import { inject } from 'vue';

/*
------------------------------------------------------------------------
Vue plugin that initializes and provides PostHog analytics.

@param {App} app - The Vue application instance.
@param {Object} options - Plugin options.
@param {Object} options.router - Vue Router instance for pageview tracking.
------------------------------------------------------------------------
*/
export default {
    install(app, options = {}) {
        //
        // Get PostHog credentials from WordPress wpData object
        // These are defined in inc/config.php and passed via wp_localize_script in inc/frontend.php
        //
        const wpData = window.wpData || {};
        const apiKey = wpData.posthogApiKey || '';
        const apiHost = wpData.posthogHost || 'https://us.i.posthog.com';

        //
        // Provide a stub when API key is missing.
        // This prevents errors when calling posthog methods.
        //
        const stubPosthog = {
            capture: () => {},
            identify: () => {},
            reset: () => {},
            isFeatureEnabled: () => false,
            getFeatureFlag: () => null,
        };

        if (!apiKey) {
            if (import.meta.env.DEV) {
                console.warn('[PostHog] No API key provided in wpData.posthogApiKey. Analytics disabled.');
            }
            app.config.globalProperties.$posthog = stubPosthog;
            app.provide('posthog', stubPosthog);
            return;
        }

        posthog.init(apiKey, {
            api_host: apiHost,
            capture_pageview: false, // We'll handle this manually via router
            capture_pageleave: true, // Capture when user leaves pages
            persistence: 'localStorage',
            autocapture: true, // Automatically capture clicks, form submissions, etc.
        });

        //
        // Register environment as a "super property" that gets attached to ALL events.
        // This allows filtering by environment in the PostHog dashboard.
        //
        const environment = wpData.environment || 'unknown';
        posthog.register({
            environment: environment,
            app_name: 'luxxbox-acoustic-analyzer',
        });

        // Make posthog available globally via this.$posthog
        app.config.globalProperties.$posthog = posthog;

        // Also provide via inject() for Composition API usage
        app.provide('posthog', posthog);

        // Set up automatic pageview tracking if router is provided
        if (options.router) {
            options.router.afterEach((to, from) => {
                // Capture pageview with route metadata
                posthog.capture('$pageview', {
                    $current_url: window.location.href,
                    route_name: to.name,
                    route_path: to.path,
                    from_route: from.name || null,
                });
            });
        }

        // Log initialization in development
        if (import.meta.env.DEV) {
            console.log('[PostHog] Initialized with host:', apiHost);
        }
    },
};

/*
------------------------------------------------------------------------
Composable for using PostHog in setup() functions.

Usage in a component's <script setup>:
  import { usePostHog } from '@/plugins/posthog';
  const posthog = usePostHog();
  posthog.capture('my_event', { property: 'value' });

@return {Object} The PostHog instance.
------------------------------------------------------------------------
*/
export function usePostHog() {
    const posthog = inject('posthog');
    if (!posthog) {
        console.warn('[PostHog] usePostHog() called but PostHog is not installed.');
        return {
            capture: () => {},
            identify: () => {},
            reset: () => {},
        };
    }
    return posthog;
}
